// Candidate assessment — taken IN THE OFFICE, in front of HR. One sitting, timed.
// HR starts it (full-screen); the candidate cannot pause, save, or come back. Closing the
// window submits whatever's written. The timer is server-authoritative (started_at on the
// attempt) — the countdown here is a display; the server auto-submits past the deadline.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

const NAVY = '#2D2C72', INK = '#3B3A8F', GREEN = '#0F7B3F', RED = '#A32D2D';
const card = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: 28 };
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export default function CandidateTest() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading');   // loading | rules | taking | done | error
  const [error, setError] = useState('');
  const [intro, setIntro] = useState(null);         // { duration_mins, mcq, written }
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [written, setWritten] = useState({});
  const [remaining, setRemaining] = useState(null); // seconds
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');   // a failed final submit, shown beside the button
  const stateRef = useRef({ answers: {}, written: {}, questions: [] });
  stateRef.current = { answers, written, questions };
  // Snapshot the written questions AS DISPLAYED and send them with the answer, so grading
  // never depends on quiz_questions rows that can be hard-deleted after submit. This is the
  // exact text/marks the candidate saw — immune to a set-edit between load and submit.
  const writtenSnapshot = () => Object.fromEntries(
    (stateRef.current.questions || []).filter(q => q.type === 'written')
      .map(q => [q.id, { question: q.question, marks: q.marks }]));
  const finished = useRef(false);

  useEffect(() => {
    api.get(`/assessment/candidate/${token}/intro`)
      .then((r) => { const d = r.data.data; if (d.done) { setPhase('done'); return; } setIntro(d); setPhase('rules'); })
      .catch((e) => { setError(e.response?.data?.error || 'This link could not be opened.'); setPhase('error'); });
  }, [token]);

  const doSubmit = useCallback(async (auto) => {
    if (finished.current) return; finished.current = true;
    setSubmitting(true);
    // A FAILED SUBMIT IS SHOWN, AND THE CANDIDATE CAN TRY AGAIN. SETTLED 2 Oct 2026 — no server change owed.
    // This catch used to be empty "because the server may have already auto-submitted on expiry". It
    // cannot have been that: POST /assessment/candidate/:token answers an already-submitted attempt
    // (auto-submit included) with 200 "Already submitted." — never an error. So everything that reaches
    // this catch is a real failure (expired link 400, rate limit 429, 500, network), and it used to show
    // the candidate ✅ anyway. A false alarm costs them an email asking whether the test went through; a
    // silent failure costs them the role and nobody ever finds out. The control fails toward telling them.
    try {
      await api.post(`/assessment/candidate/${token}`, { answers: stateRef.current.answers, written_answers: stateRef.current.written, written_snapshot: writtenSnapshot() });
    } catch (e) {
      finished.current = false; setSubmitting(false);
      setSubmitError(`Your answers were NOT submitted — ${e.response?.data?.error || 'the server could not be reached'}. ` +
        'They are still on this page. Press "Submit assessment" to try again; if it keeps failing, tell the HR contact who sent you this link.');
      return;
    }
    setPhase('done');
  }, [token]);

  // Start the test (this stamps started_at server-side → the clock is now running).
  const start = async () => {
    try { document.documentElement.requestFullscreen?.().catch(() => {}); } catch { /* ignore */ }
    try {
      const r = await api.get(`/assessment/candidate/${token}`);
      const d = r.data.data;
      if (d.done) { setPhase('done'); return; }
      setQuestions(d.questions || []);
      setRemaining(d.remaining_seconds ?? (intro.duration_mins * 60));
      setPhase('taking');
    } catch (e) { setError(e.response?.data?.error || 'Could not start the test.'); setPhase('error'); }
  };

  // Countdown → auto-submit at 0.
  useEffect(() => {
    if (phase !== 'taking' || remaining == null) return;
    if (remaining <= 0) { doSubmit(true); return; }
    const t = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, remaining, doSubmit]);

  // Silent autosave (backup for the server's copy) + submit-on-close via sendBeacon.
  useEffect(() => {
    if (phase !== 'taking') return;
    // write-error-ok: silent autosave during a candidate's timed test; the server copy is a backup and submit is the real save
    const t = setTimeout(() => { api.patch(`/assessment/candidate/${token}/draft`, { answers, written_answers: written, written_snapshot: writtenSnapshot() }).catch(() => {}); }, 2000);
    return () => clearTimeout(t);
  }, [answers, written, phase, token]);
  useEffect(() => {
    if (phase !== 'taking') return;
    const beacon = () => {
      if (finished.current) return;
      try {
        const url = (api.defaults.baseURL || '') + `/assessment/candidate/${token}`;
        const body = new Blob([JSON.stringify({ answers: stateRef.current.answers, written_answers: stateRef.current.written, written_snapshot: writtenSnapshot() })], { type: "application/json" });
        navigator.sendBeacon?.(url, body);   // closing the window submits whatever's written
      } catch { /* ignore */ }
    };
    const onHide = () => { if (document.visibilityState === 'hidden') beacon(); };
    window.addEventListener('pagehide', beacon);
    document.addEventListener('visibilitychange', onHide);
    return () => { window.removeEventListener('pagehide', beacon); document.removeEventListener('visibilitychange', onHide); };
  }, [phase, token]);

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: '#F4F4F8', display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
      <div style={{ width: '100%', maxWidth: 720 }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>RSR International</div>
          <div style={{ fontSize: 13, color: '#888' }}>Candidate Assessment</div>
        </div>
        {children}
      </div>
    </div>
  );

  if (phase === 'loading') return shell(<div style={{ ...card, textAlign: 'center', color: '#999' }}>Loading…</div>);
  if (phase === 'error') return shell(<div style={{ ...card, textAlign: 'center' }}><div style={{ fontSize: 40 }}>🔗</div><div style={{ fontWeight: 700, color: RED, margin: '10px 0' }}>{error}</div><div style={{ color: '#666', fontSize: 14 }}>Please ask the HR team to start the test.</div></div>);
  if (phase === 'done') return shell(<div style={{ ...card, textAlign: 'center' }}><div style={{ fontSize: 44 }}>✅</div><div style={{ fontWeight: 800, fontSize: 18, color: GREEN, margin: '12px 0' }}>Submitted — thank you.</div><div style={{ color: '#666', fontSize: 14 }}>Your responses are with our team.</div></div>);

  if (phase === 'rules') {
    const rule = (icon, text) => <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 13, fontSize: 15, color: '#333' }}><span style={{ fontSize: 18 }}>{icon}</span><span>{text}</span></li>;
    return shell(
      <div style={card}>
        <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, marginBottom: 4 }}>Read this before you begin</div>
        <div style={{ color: '#777', fontSize: 13, marginBottom: 18 }}>{intro.mcq} multiple-choice + {intro.written} written questions.</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {rule('⏱', <>You have <b>{intro.duration_mins} minutes</b>. The timer starts the moment you press Begin and does not stop.</>)}
          {rule('🚫', <><b>One sitting.</b> You cannot pause, save, or come back to this test.</>)}
          {rule('⚠️', <><b>Closing this window ends your test</b> — whatever you have written is submitted as your final answer.</>)}
          {rule('✍️', <>Answer in your own words. The written questions matter most.</>)}
        </ul>
        <button onClick={start}
          style={{ marginTop: 22, width: '100%', padding: '14px', background: INK, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
          Begin — start the {intro.duration_mins}-minute timer
        </button>
      </div>
    );
  }

  // taking
  const low = remaining != null && remaining <= 120;
  let mcqN = 0, wrN = 0;
  return shell(
    <div>
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: low ? '#FCEBEB' : '#fff', borderRadius: 10, boxShadow: '0 1px 8px rgba(0,0,0,0.08)', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: '#666' }}>Answered {questions.filter(q => q.type === 'mcq' ? typeof answers[q.id] === 'number' : (written[q.id] || '').trim()).length} / {questions.length}</span>
        <span style={{ fontSize: 20, fontWeight: 800, color: low ? RED : NAVY, fontVariantNumeric: 'tabular-nums' }}>⏱ {remaining != null ? mmss(remaining) : '—'}</span>
      </div>

      {questions.map((q) => {
        const n = q.type === 'mcq' ? ++mcqN : ++wrN;
        return (
          <div key={q.id} style={{ ...card, marginBottom: 14, padding: 20 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: q.type === 'mcq' ? INK : '#B45309', borderRadius: 6, padding: '2px 8px' }}>{q.type === 'mcq' ? `Q${n}` : `Written ${n}`}</span>
              {q.type === 'written' && <span style={{ fontSize: 11, color: '#999' }}>{q.marks} marks</span>}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#222', marginBottom: 14, lineHeight: 1.5 }}>{q.question}</div>
            {q.type === 'mcq' ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {q.options.map((opt, i) => (
                  <label key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', border: `1px solid ${answers[q.id] === i ? INK : '#e2e2e8'}`, background: answers[q.id] === i ? '#EEEDF8' : '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input type="radio" name={q.id} checked={answers[q.id] === i} onChange={() => setAnswers((a) => ({ ...a, [q.id]: i }))} />
                    {opt}
                  </label>
                ))}
              </div>
            ) : (
              <textarea value={written[q.id] || ''} onChange={(e) => setWritten((w) => ({ ...w, [q.id]: e.target.value }))}
                placeholder="Type your answer here…" rows={6}
                style={{ width: '100%', padding: '11px 13px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            )}
          </div>
        );
      })}

      <div style={{ ...card, padding: 18, textAlign: 'right' }}>
        {submitError && <div style={{ textAlign: 'left', background: '#FCEBEB', color: RED, border: '1px solid #f0cccc', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 14, fontWeight: 600 }}>{submitError}</div>}
        <button onClick={() => { if (window.confirm('Submit your answers? You cannot change them after this.')) { setSubmitError(''); doSubmit(false); } }} disabled={submitting}
          style={{ padding: '12px 28px', background: submitting ? '#9aa' : GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: submitting ? 'default' : 'pointer' }}>
          {submitting ? 'Submitting…' : 'Submit assessment'}
        </button>
      </div>
    </div>
  );
}
